/**
 * CopyButton — copy-to-clipboard with a confirmation that decays.
 *
 * The report's whole "what to do" half is meant to leave the page: questions
 * get pasted into a planning meeting, tasks into a tracker. So copying is a
 * first-class action, not a convenience.
 *
 * Wraps the design system Button; only the transient success state is local
 * (D12).
 *
 * @format
 */

import {useEffect, useRef, useState} from 'react';

import {Button, type ButtonProps} from 'ui/components/atoms/Button';

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Text placed on the clipboard. */
  value: string;
  /** Idle label. */
  label: string;
  /** Label shown briefly after a successful copy. */
  doneLabel?: string;
}

export function CopyButton({
  value,
  label,
  doneLabel = 'Скопировано',
  ...buttonProps
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      {...buttonProps}
      color={copied ? 'good' : buttonProps.color}
      leftIcon={copied ? 'check' : 'content_copy'}
      onClick={handleClick}
      // Reserve the widest label so the button doesn't resize mid-interaction.
      stableLabels={[label, doneLabel]}
    >
      {copied ? doneLabel : label}
    </Button>
  );
}
